import { useEffect, useState, useMemo } from 'react';
import { isOpenMatterStatus } from '@/lib/matterStatus';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  Briefcase,
  Scales as Scale,
  CalendarBlank,
  Buildings as Building2,
  Warning,
  Sparkle,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useMattersStore } from '@/lib/mattersStore';
import { useHearingsStore } from '@/lib/hearingsStore';
import { useDiaryStore, dateKey, todayKey as getTodayKey } from '@/lib/diaryStore';
import { nowLocalTime } from '@/lib/datetime';
import { useAuthStore } from '@/features/auth/store';
import { useClerkWorkspace } from '@/lib/useClerkWorkspace';
import { useApiBarristersStore } from '@/lib/barristersApi';
import { fetchCourts, type Court } from '@/lib/courtsApi';
import { canSeeMatter, type PrincipalLite } from '@/lib/permissions';
import { useBarristerBusy } from '@/lib/useBarristerBusy';
import { DoubleBookConfirm } from '@/components/shared/DoubleBookConfirm';

/** Bookable hour slots for the day grid (09:00–17:00). */
const TIME_SLOTS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];

const STEPS = [
  { id: 1, label: 'Matter',      icon: Briefcase     },
  { id: 2, label: 'Barrister', icon: Scale         },
  { id: 3, label: 'Date',      icon: CalendarBlank },
  { id: 4, label: 'Court',     icon: Building2     },
  { id: 5, label: 'Confirm',   icon: CheckCircle   },
];

/** Total day slots (09:00–17:00 = 8 one-hour slots) */
const TOTAL_SLOTS = 8;

export default function SchedulePage() {
  const [step, setStep] = useState(1);

  // Default date to tomorrow in yyyy-MM-dd format
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = dateKey(tomorrow);

  const [picked, setPicked] = useState({ case: '', barrister: '', barristerId: '', date: defaultDate, time: '', court: '' });
  const navigate = useNavigate();

  const matters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  const addHearing = useHearingsStore((s) => s.addHearing);
  const diaryStore = useDiaryStore();
  const fetchEvents = useDiaryStore((s) => s.fetchEvents);
  const apiBarristers = useApiBarristersStore((s) => s.barristers);
  const fetchBarristers = useApiBarristersStore((s) => s.fetch);
  const [courts, setCourts] = useState<Court[]>([]);

  useEffect(() => { fetchMatters(); fetchBarristers(); fetchEvents(); }, [fetchMatters, fetchBarristers, fetchEvents]);
  useEffect(() => { fetchCourts().then((c) => setCourts(c.filter((x) => x.isActive))).catch(() => {}); }, []);
  const user = useAuthStore((s) => s.user);
  const clerkFullName = user ? `${user.firstName} ${user.lastName}` : '';
  const { managedBarristers } = useClerkWorkspace();
  const principal: PrincipalLite | null = user
    ? { name: clerkFullName, role: 'clerk', memberType: user.memberType }
    : null;

  // Active matters = not closed AND clerk has access
  const activeMatters = useMemo(
    () => matters.filter((m) => isOpenMatterStatus(m.status) && canSeeMatter(principal, m, managedBarristers)),
    [matters, principal, managedBarristers],
  );

  // Compute barristers with availability + match %
  const todayKey = getTodayKey();
  const suggestedBarristers = useMemo(() => {
    const selectedMatter = activeMatters.find((m) => m.id === picked.case);
    const matterArea = selectedMatter?.area ?? '';

    return apiBarristers.map((bp) => {
      const name = `${bp.user.firstName} ${bp.user.lastName}${bp.user.memberType === 'silk' ? ' KC' : ''}`;
      const areas = bp.practiceAreas?.map((pa: any) => pa.name) ?? [];
      // Count free slots today for this barrister
      const todayEvents = diaryStore.getEventsForDate(todayKey).filter((e) => e.barrister === name);
      const busyHours = todayEvents.reduce((sum, e) => sum + e.span, 0);
      const freeSlots = Math.max(0, TOTAL_SLOTS - busyHours);
      const available = freeSlots > 0;

      // Match % based on practice area overlap
      const match = matterArea && areas.includes(matterArea)
        ? Math.min(98, 80 + Math.round((freeSlots / TOTAL_SLOTS) * 18))
        : Math.min(70, 40 + Math.round((freeSlots / TOTAL_SLOTS) * 30));

      return { id: bp.id, name, role: bp.user.memberType, areas, freeSlots, available, match };
    }).sort((a, b) => b.match - a.match);
  }, [activeMatters, picked.case, diaryStore, todayKey]);

  const caseObj = activeMatters.find((c) => c.id === picked.case);
  const barristerObj = suggestedBarristers.find((b) => b.id === picked.barristerId);
  const courtObj = courts.find((c) => c.id === picked.court);

  // Opaque free/busy for the picked barrister — the ONLY source that includes the barrister's
  // PRIVATE events (a clerk's own diary store never sees them). Drives BOTH the availability grid
  // and the double-book guard, so the clerk sees a barrister's blocked time (e.g. a personal 5pm
  // hold) and is warned before booking over it. Warn-never-block, keyed on the assigned barrister.
  const busyAnchors = useMemo(() => {
    if (!picked.barristerId || !picked.date) return [] as string[];
    const anchors = [`${picked.date}T09:00:00Z`];
    if (picked.time) anchors.push(`${picked.date}T${picked.time}:00Z`);
    return anchors;
  }, [picked.barristerId, picked.date, picked.time]);
  const { getConflicts } = useBarristerBusy(picked.barristerId || null, busyAnchors);

  // A slot is occupied when a booking there (60 min) overlaps the barrister's diary. buffer=0 so
  // only real overlaps mark a slot busy — adjacent slots stay bookable. Times are wall-clock-in-UTC
  // (`...:00Z`), matching how events are stored/read app-wide; a local ISO would shift the check by
  // the browser offset and flag the wrong hour for clerks in non-UTC zones.
  const isSlotBusy = (time: string) =>
    getConflicts(`${picked.date}T${time}:00Z`, 60, 0).some((c) => c.kind === 'overlap');

  const bookingOverlaps = useMemo(
    () =>
      picked.date && picked.time
        ? getConflicts(`${picked.date}T${picked.time}:00Z`, 60, 0).filter((c) => c.kind === 'overlap')
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [picked.date, picked.time, getConflicts],
  );
  const [confirmClash, setConfirmClash] = useState(false);

  function handleCreateBooking() {
    if (!caseObj || !barristerObj) return;
    // Double-book: warn (never block) when the chosen slot overlaps the barrister's diary. The clerk
    // explicitly confirms to proceed — identical pattern to the Master Diary allocate flow.
    if (bookingOverlaps.length > 0) { setConfirmClash(true); return; }
    doCreateBooking();
  }

  const [booking, setBooking] = useState(false);

  async function doCreateBooking() {
    if (!caseObj || !barristerObj || booking) return;

    const clerkName = user ? `${user.firstName} ${user.lastName}` : 'Clerk';
    const courtCode = courtObj?.shortName ?? '';

    // Wait for the server: "Hearing booked" used to show (and the page moved on) before the API
    // answered, so a refused booking looked booked and then silently vanished from the list.
    setBooking(true);
    const created = await addHearing({
      matterId: caseObj.id,
      date: picked.date,
      time: picked.time,
      durationLabel: 'Full day',
      type: 'Hearing',
      courtId: courtObj?.id,
      court: courtCode,
      courtroom: '',
      judge: '',
      leadBarrister: barristerObj.name,
      juniorBarrister: '',
      isVirtual: false,
      videoLink: '',
      notes: `Booked via schedule wizard by ${clerkName}`,
    });
    setBooking(false);
    if (!created) return; // the store has already told the clerk why

    toast.success(`Hearing booked for ${caseObj.name} — ${barristerObj.name} on ${picked.date}`);
    navigate('/clerk/hearings');
  }

  return (
    <div>
      <PageHeader
        title="Schedule a hearing"
        subtitle="Allocate counsel and book a court date in one flow"
      />

      {/* Stepper */}
      <Card className="mb-6 p-4">
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex flex-1 items-center">
              <button
                onClick={() => setStep(s.id)}
                className="flex flex-col items-center gap-2"
              >
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full transition ${
                    step >= s.id
                      ? 'bg-brand-600 text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {step > s.id ? <CheckCircle className="h-5 w-5" weight="fill" /> : <s.icon className="h-5 w-5" />}
                </div>
                <div className={`text-xs font-semibold uppercase tracking-wider ${
                  step >= s.id ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {s.label}
                </div>
              </button>
              {i < STEPS.length - 1 && (
                <div className={`mx-2 mb-6 h-px flex-1 ${step > s.id ? 'bg-brand-600' : 'bg-border'}`} />
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="p-6">
          {step === 1 && (
            <div>
              <h3 className="mb-1 text-lg font-semibold text-foreground">Pick a matter</h3>
              <p className="mb-4 text-sm text-muted-foreground">Choose from the booking inbox or open caseload</p>
              <div className="space-y-2">
                {activeMatters.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setPicked({ ...picked, case: c.id })}
                    className={`flex w-full items-center justify-between rounded-md border px-4 py-3 text-left transition ${
                      picked.case === c.id ? 'border-brand-600 bg-brand-50' : 'border-border hover:border-brand-200 hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-brand-700 dark:text-brand-300">{c.number}</div>
                      <div className="text-sm font-medium text-foreground">{c.name}</div>
                      <Badge>{c.area}</Badge>
                    </div>
                    <div className="tabular text-sm font-semibold text-foreground">£{c.feeEstimate.toLocaleString()}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="mb-1 text-lg font-semibold text-foreground">Suggested counsel</h3>
              <p className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground">
                <Sparkle className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400" weight="fill" />
                Ranked by practice area match and current availability
              </p>
              <div className="space-y-2">
                {suggestedBarristers.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setPicked({ ...picked, barrister: b.name, barristerId: b.id })}
                    disabled={!b.available}
                    className={`flex w-full items-center justify-between rounded-md border px-4 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                      picked.barristerId === b.id ? 'border-brand-600 bg-brand-50' : 'border-border hover:border-brand-200 hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-sm font-semibold text-brand-700 dark:text-brand-300">
                        {b.name.split(' ').map((p) => p[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground">{b.name}</div>
                        <div className="text-xs text-muted-foreground">{b.role} · {b.freeSlots} free slots today</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={b.available ? 'success' : 'default'}>
                        {b.available ? 'Available' : 'Unavailable'}
                      </Badge>
                      <div className="tabular text-xs font-semibold text-brand-600 dark:text-brand-400">{b.match}% match</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (() => {
            // Occupancy comes from the opaque free/busy feed (includes the barrister's private
            // events), not the clerk's diary store — otherwise a barrister's personal hold shows as
            // "Free" and the clerk books over it unaware.
            const slotBusy: boolean[] = picked.barristerId && picked.date
              ? TIME_SLOTS.map((time) => isSlotBusy(time))
              : TIME_SLOTS.map(() => false);
            // When today is selected, slots earlier than the current time are in the past.
            const isTodaySelected = picked.date === todayKey;
            const nowHHMM = nowLocalTime();

            return (
              <div>
                <h3 className="mb-1 text-lg font-semibold text-foreground">Pick a date & time</h3>
                <p className="mb-4 text-sm text-muted-foreground">
                  {picked.barrister
                    ? `Showing ${picked.barrister}'s availability. Grey slots are occupied.`
                    : 'Select a barrister first to see availability.'}
                </p>

                {/* Date picker */}
                <div className="mb-4">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</label>
                  <input
                    type="date"
                    value={picked.date}
                    min={getTodayKey()}
                    onChange={(e) => setPicked({ ...picked, date: e.target.value, time: '' })}
                    className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  />
                </div>

                {/* Visual time slot grid */}
                {picked.barrister && picked.date && (
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Time slot — {picked.barrister} · {new Date(picked.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {TIME_SLOTS.map((time, idx) => {
                        const occupied = slotBusy[idx];
                        const isSelected = picked.time === time;
                        const isPast = isTodaySelected && time < nowHHMM;

                        if (isPast) {
                          return (
                            <div
                              key={time}
                              className="cursor-not-allowed rounded-md border border-border bg-muted p-2.5 opacity-60"
                              title="This time has already passed"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">{time}</span>
                                <Badge variant="default">Past</Badge>
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">Unavailable</div>
                            </div>
                          );
                        }

                        if (occupied) {
                          return (
                            <div
                              key={time}
                              className="cursor-not-allowed rounded-md border border-border bg-muted p-2.5 opacity-70"
                              title="This barrister is already booked at this time"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">{time}</span>
                                <Badge variant="default">Busy</Badge>
                              </div>
                              {/* Opaque: the clerk sees only that the time is taken, never the detail. */}
                              <div className="mt-1 text-xs font-medium text-muted-foreground">Unavailable</div>
                            </div>
                          );
                        }

                        return (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setPicked({ ...picked, time })}
                            className={`rounded-md border p-2.5 text-left transition ${
                              isSelected
                                ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-200'
                                : 'border-border hover:border-brand-300 hover:bg-muted/40'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-foreground">{time}</span>
                              <Badge variant="success">Free</Badge>
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">Available</div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Summary */}
                    <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded bg-emerald-500" />
                        {slotBusy.filter((b) => !b).length} free
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded bg-slate-300" />
                        {slotBusy.filter((b) => b).length} occupied
                      </span>
                      {picked.time && (
                        <span className="ml-auto font-semibold text-brand-600 dark:text-brand-400">
                          Selected: {picked.time}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {!picked.barrister && (
                  <div className="mt-4 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300">
                    <Warning className="mr-1.5 inline h-3.5 w-3.5" />
                    Go back to Step 2 and select a barrister to see their availability.
                  </div>
                )}
              </div>
            );
          })()}

          {step === 4 && (
            <div>
              <h3 className="mb-1 text-lg font-semibold text-foreground">Pick a court</h3>
              <p className="mb-4 text-sm text-muted-foreground">Court venue and case track</p>
              <div className="space-y-2">
                {courts.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setPicked({ ...picked, court: c.id })}
                    className={`flex w-full items-center justify-between rounded-md border px-4 py-3 text-left transition ${
                      picked.court === c.id ? 'border-brand-600 bg-brand-50' : 'border-border hover:border-brand-200 hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="text-xs text-brand-700 dark:text-brand-300">{c.shortName}</div>
                        <div className="text-sm font-medium text-foreground">{c.name}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h3 className="mb-1 text-lg font-semibold text-foreground">Confirm</h3>
              <p className="mb-4 text-sm text-muted-foreground">Review and create the booking</p>
              <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4">
                <Row label="Matter"      value={caseObj ? `${caseObj.number} — ${caseObj.name}` : '—'} />
                <Row label="Counsel"   value={barristerObj ? `${barristerObj.name} (${barristerObj.role})` : '—'} />
                <Row label="Date"      value={picked.date} />
                <Row label="Time"      value={picked.time} />
                <Row label="Court"     value={courtObj ? `${courtObj.shortName} — ${courtObj.name}` : '—'} />
                <Row label="Brief fee" value={caseObj ? `£${caseObj.feeEstimate.toLocaleString()}` : '—'} bold />
              </div>

              {bookingOverlaps.length > 0 && (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300">
                  <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0" weight="fill" />
                  <span>
                    {barristerObj?.name ?? 'This barrister'} is already booked at {picked.time} on {picked.date}.
                    You can still book over it — you'll be asked to confirm.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <Button
            variant="outline"
            size="md"
            disabled={step === 1}
            onClick={() => setStep(Math.max(1, step - 1))}
            leftIcon={<CaretLeft className="h-4 w-4" />}
          >
            Back
          </Button>
          {step < 5 ? (
            <Button
              variant="primary"
              size="md"
              onClick={() => setStep(step + 1)}
              rightIcon={<CaretRight className="h-4 w-4" />}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              leftIcon={<CheckCircle className="h-4 w-4" />}
              onClick={handleCreateBooking}
              disabled={booking}
            >
              {booking ? 'Booking…' : 'Create booking'}
            </Button>
          )}
        </div>
      </Card>

      {confirmClash && (
        <DoubleBookConfirm
          clashes={bookingOverlaps.map((c) => ({ title: c.event.title, startAt: c.event.startAt, endAt: c.event.endAt }))}
          onConfirm={() => { setConfirmClash(false); doCreateBooking(); }}
          onCancel={() => setConfirmClash(false)}
        />
      )}
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={`${bold ? 'tabular text-base font-semibold' : 'font-medium'} text-foreground`}>{value}</span>
    </div>
  );
}
